import { useCallback, useMemo } from "react";
import useSimpleLocalStorage from "@/hooks/useSimpleLocalStorage";

type ExportColumnsStorage<ColumnId extends string> = Record<string, ColumnId[]>;

/**
 * Hook for persisting selected export columns per user and per context.
 * Uses LocalStorage. At larger scale we should consider IndexedDB or moving to BE.
 */
export function useExportColumns<ColumnId extends string>(
  storageKey: string,
  contextId: string,
) {
  const [storage, setStorage] = useSimpleLocalStorage<
    ExportColumnsStorage<ColumnId>
  >(storageKey, {});

  const selectedColumns = useMemo(
    () => storage[contextId] ?? [],
    [storage, contextId],
  );

  const toggleColumn = useCallback(
    (columnId: ColumnId) => {
      setStorage((prev) => {
        const columns = new Set(prev[contextId] ?? []);

        if (columns.has(columnId)) {
          columns.delete(columnId);
        } else {
          columns.add(columnId);
        }

        return {
          ...prev,
          [contextId]: Array.from(columns),
        };
      });
    },
    [contextId, setStorage],
  );

  return {
    selectedColumns,
    toggleColumn,
  };
}
